import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiEdit2, FiCheck, FiX, FiCamera, FiBookmark, FiClock } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useMovieContext } from '../context/MovieContext';
import Footer from '../components/Footer';

const ProfilePage = () => {
  const { user, profile, updateUserProfile } = useAuth();
  const { watchlist } = useMovieContext();
  const [editing,     setEditing]     = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [photoURL,    setPhotoURL]    = useState(user?.photoURL    || '');
  const [bio,         setBio]         = useState(profile?.bio      || '');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState(false);

  const avatar   = user?.photoURL;
  const initials = (user?.displayName || user?.email || 'U')[0].toUpperCase();

  const startEdit = () => {
    setDisplayName(user?.displayName || '');
    setPhotoURL(user?.photoURL || '');
    setBio(profile?.bio || '');
    setError(''); setSuccess(false); setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setError(''); };

  const saveEdit = async () => {
    if (!displayName.trim()) { setError('Display name cannot be empty.'); return; }
    setSaving(true); setError('');
    try {
      await updateUserProfile({ displayName: displayName.trim(), photoURL: photoURL.trim(), bio: bio.trim() });
      setSuccess(true); setEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch { setError('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt.seconds * 1000).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Recently';

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh', paddingTop: 88 }}>
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-10">

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-display text-4xl md:text-5xl tracking-wide" style={{ color: 'var(--text)' }}>My Profile</h1>
        </motion.div>

        {/* Profile card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-2xl p-7 mb-6"
          style={{ background: 'rgba(17,17,24,0.85)', border: '1px solid rgba(255,255,255,0.07)' }}>

          {/* Avatar + info */}
          <div className="flex items-start gap-5 mb-6">
            <div className="relative flex-shrink-0">
              {avatar
                ? <img src={avatar} alt="" className="w-20 h-20 rounded-full object-cover ring-2" style={{ ringColor: 'var(--red)' }} />
                : <div className="w-20 h-20 rounded-full flex items-center justify-center font-display text-3xl"
                    style={{ background: 'var(--red)', color: '#fff' }}>{initials}</div>
              }
              {editing && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--red)', border: '2px solid var(--dark)' }}>
                  <FiCamera size={12} color="#fff" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              {!editing ? (
                <>
                  <h2 className="font-display text-2xl leading-tight" style={{ color: 'var(--text)' }}>
                    {user?.displayName || 'Anonymous'}
                  </h2>
                  <p className="text-sm mt-1 font-body" style={{ color: 'var(--muted)' }}>{user?.email}</p>
                  {profile?.bio && (
                    <p className="text-sm mt-2 font-body leading-relaxed" style={{ color: 'rgba(232,232,240,0.7)' }}>{profile.bio}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    <span className="text-xs font-accent" style={{ color: 'var(--muted)' }}>
                      Member since {memberSince}
                    </span>
                    {profile?.isAdmin && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-accent"
                        style={{ background: 'rgba(229,9,20,0.2)', color: 'var(--red)', border: '1px solid rgba(229,9,20,0.3)' }}>
                        Admin
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-3 w-full">
                  <div>
                    <label className="text-xs font-accent uppercase tracking-wider mb-1 block" style={{ color: 'var(--muted)' }}>Display Name</label>
                    <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl text-sm font-body outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(229,9,20,0.4)', color: 'var(--text)' }} />
                  </div>
                  <div>
                    <label className="text-xs font-accent uppercase tracking-wider mb-1 block" style={{ color: 'var(--muted)' }}>Photo URL</label>
                    <input value={photoURL} onChange={e => setPhotoURL(e.target.value)}
                      placeholder="https://…"
                      className="w-full px-3 py-2 rounded-xl text-sm font-body outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text)' }} />
                  </div>
                  <div>
                    <label className="text-xs font-accent uppercase tracking-wider mb-1 block" style={{ color: 'var(--muted)' }}>Bio</label>
                    <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3}
                      placeholder="Tell us about yourself…"
                      className="w-full px-3 py-2 rounded-xl text-sm font-body outline-none resize-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text)' }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error / success */}
          <AnimatePresence>
            {error && (
              <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="text-xs mb-3 font-body px-3 py-2 rounded-lg"
                style={{ color: '#ff6b6b', background: 'rgba(229,9,20,0.1)' }}>{error}</motion.p>
            )}
            {success && (
              <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="text-xs mb-3 font-body px-3 py-2 rounded-lg"
                style={{ color: '#6bff9e', background: 'rgba(0,200,100,0.1)' }}>Profile updated!</motion.p>
            )}
          </AnimatePresence>

          {/* Action buttons */}
          <div className="flex gap-3">
            {!editing ? (
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={startEdit}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-body font-medium"
                style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 20px rgba(229,9,20,0.3)' }}>
                <FiEdit2 size={14} /> Edit Profile
              </motion.button>
            ) : (
              <>
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={saveEdit} disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-body font-medium"
                  style={{ background: 'var(--red)', color: '#fff', opacity: saving ? 0.6 : 1 }}>
                  <FiCheck size={14} /> {saving ? 'Saving…' : 'Save Changes'}
                </motion.button>
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={cancelEdit}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-body glass hover:bg-white/10 transition-colors"
                  style={{ color: 'var(--muted)' }}>
                  <FiX size={14} /> Cancel
                </motion.button>
              </>
            )}
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="grid grid-cols-2 gap-4">
          {[
            { icon: FiBookmark, label: 'Watchlist',         value: watchlist.length,  color: 'var(--red)' },
            { icon: FiClock,    label: 'Continue Watching', value: '—',               color: '#00C9FF' },
          ].map(s => (
            <div key={s.label} className="rounded-2xl p-5 glass">
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={15} style={{ color: s.color }} />
                <span className="text-xs font-accent uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{s.label}</span>
              </div>
              <p className="font-display text-3xl" style={{ color: 'var(--text)' }}>{s.value}</p>
            </div>
          ))}
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePage;
